import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Users, Plus, DollarSign, MousePointerClick, TrendingUp, Check, X, Loader2, Copy, Pencil, Trash2, Banknote, CreditCard } from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#1A9DA5]/10 text-[#1A9DA5]"><Icon className="w-5 h-5" /></div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Affiliates() {
  const [affiliates, setAffiliates] = useState([]);
  const [stats, setStats] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [editAffiliate, setEditAffiliate] = useState(null);
  const [payoutTarget, setPayoutTarget] = useState(null);
  const [payableData, setPayableData] = useState(null);
  const [tab, setTab] = useState('affiliates');
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '', website: '', commission_rate: 10, status: 'active' });
  const [payoutForm, setPayoutForm] = useState({ amount: 0, method: '', notes: '', commission_ids: [] });

  const load = async () => {
    try {
      const [affRes, statsRes, commRes, payRes] = await Promise.all([
        api.get('/admin/affiliates'),
        api.get('/admin/affiliates/stats'),
        api.get('/admin/commissions'),
        api.get('/admin/payouts'),
      ]);
      setAffiliates(affRes.data);
      setStats(statsRes.data);
      setCommissions(commRes.data);
      setPayouts(payRes.data);
    } catch (err) {
      toast.error('Failed to load affiliates');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      if (editAffiliate) {
        const payload = {
          name: form.name, company: form.company, website: form.website,
          commission_rate: parseFloat(form.commission_rate), status: form.status,
        };
        if (form.new_password?.trim()) payload.new_password = form.new_password;
        await api.put(`/admin/affiliates/${editAffiliate.id}`, payload);
        toast.success('Affiliate updated');
      } else {
        await api.post('/admin/affiliates', form);
        toast.success('Affiliate created');
      }
      setDialogOpen(false);
      setEditAffiliate(null);
      setForm({ name: '', email: '', password: '', company: '', website: '', commission_rate: 10, status: 'active', new_password: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this affiliate?')) return;
    try {
      await api.delete(`/admin/affiliates/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleStatusToggle = async (aff) => {
    const newStatus = aff.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/admin/affiliates/${aff.id}`, { status: newStatus });
      toast.success(`Affiliate ${newStatus}`);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const handleCommissionStatus = async (commissionId, status) => {
    try {
      await api.put(`/admin/commissions/${commissionId}/status`, { status });
      toast.success(`Commission ${status}`);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const openEdit = (aff) => {
    setEditAffiliate(aff);
    setForm({ name: aff.name, email: aff.email, password: '', company: aff.company || '', website: aff.website || '', commission_rate: aff.commission_rate, status: aff.status, new_password: '' });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditAffiliate(null);
    setForm({ name: '', email: '', password: '', company: '', website: '', commission_rate: 10, status: 'active', new_password: '' });
    setDialogOpen(true);
  };

  const openPayoutDialog = async (aff) => {
    setPayoutTarget(aff);
    setPayoutForm({ amount: 0, method: aff.payment_method || '', notes: '', commission_ids: [] });
    try {
      const { data } = await api.get(`/admin/affiliates/${aff.id}/payable`);
      setPayableData(data);
      setPayoutForm(f => ({
        ...f,
        amount: data.total_payable,
        commission_ids: data.commissions.map(c => c.id),
      }));
    } catch { setPayableData({ commissions: [], total_payable: 0 }); }
    setPayoutDialogOpen(true);
  };

  const handleCreatePayout = async () => {
    if (!payoutTarget || payoutForm.amount <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await api.post('/admin/payouts', {
        affiliate_id: payoutTarget.id,
        amount: parseFloat(payoutForm.amount),
        method: payoutForm.method,
        notes: payoutForm.notes,
        commission_ids: payoutForm.commission_ids,
      });
      toast.success(`Payout of $${parseFloat(payoutForm.amount).toFixed(2)} recorded`);
      setPayoutDialogOpen(false);
      setPayoutTarget(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create payout'); }
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}?ref=${code}`);
    toast.success('Referral link copied');
  };

  const statusColor = (s) => {
    if (s === 'active') return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
    if (s === 'pending') return 'bg-amber-500/10 text-amber-600 border-amber-500/20';
    return 'bg-red-500/10 text-red-500 border-red-500/20';
  };

  const commissionStatusColor = (s) => {
    if (s === 'paid') return 'bg-emerald-500/10 text-emerald-600';
    if (s === 'approved') return 'bg-blue-500/10 text-blue-600';
    return 'bg-amber-500/10 text-amber-600';
  };

  const paymentMethodLabel = (aff) => {
    if (aff.payment_method === 'paypal' && aff.paypal_email) return `PayPal: ${aff.paypal_email}`;
    if (aff.payment_method === 'bank' && aff.bank_name) return `Bank: ${aff.bank_name}`;
    return 'Not set';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#1A9DA5]" /></div>;

  return (
    <div className="space-y-6" data-testid="affiliates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Affiliates</h1>
          <p className="text-sm text-muted-foreground">Manage partners, commissions, and payouts</p>
        </div>
        <Button onClick={openCreate} className="bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="create-affiliate-btn">
          <Plus className="w-4 h-4 mr-1" />Add Affiliate
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Total Affiliates" value={stats.total_affiliates} sub={`${stats.active_affiliates} active, ${stats.pending_affiliates} pending`} />
          <StatCard icon={MousePointerClick} label="Total Clicks" value={stats.total_clicks} />
          <StatCard icon={TrendingUp} label="Conversions" value={stats.total_commissions} />
          <StatCard icon={DollarSign} label="Total Commissions" value={`$${stats.total_amount.toFixed(2)}`} sub={`$${stats.paid_amount.toFixed(2)} paid out`} />
        </div>
      )}

      <div className="flex gap-2 border-b">
        {['affiliates', 'commissions', 'payouts'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${tab === t ? 'border-[#1A9DA5] text-[#1A9DA5]' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === 'affiliates' && (
        <div className="space-y-3">
          {affiliates.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No affiliates yet.</CardContent></Card>
          ) : affiliates.map(aff => (
            <Card key={aff.id} data-testid={`affiliate-card-${aff.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{aff.name}</h3>
                      <Badge className={statusColor(aff.status)}>{aff.status}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{aff.email}</p>
                    {aff.company && <p className="text-xs text-muted-foreground">{aff.company}</p>}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <CreditCard className="w-3 h-3 inline mr-1" />{paymentMethodLabel(aff)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center"><p className="font-bold">{aff.commission_rate}%</p><p className="text-xs text-muted-foreground">Rate</p></div>
                    <div className="text-center"><p className="font-bold">{aff.total_referrals || 0}</p><p className="text-xs text-muted-foreground">Clicks</p></div>
                    <div className="text-center"><p className="font-bold">{aff.total_conversions || 0}</p><p className="text-xs text-muted-foreground">Sales</p></div>
                    <div className="text-center"><p className="font-bold text-[#1A9DA5]">${(aff.total_earnings || 0).toFixed(2)}</p><p className="text-xs text-muted-foreground">Earned</p></div>
                    <div className="text-center"><p className="font-bold text-emerald-600">${(aff.total_paid || 0).toFixed(2)}</p><p className="text-xs text-muted-foreground">Paid</p></div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => openPayoutDialog(aff)} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" data-testid={`payout-btn-${aff.id}`}>
                      <Banknote className="w-4 h-4 mr-1" />Payout
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => copyLink(aff.referral_code)} title="Copy referral link"><Copy className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(aff)}><Pencil className="w-4 h-4" /></Button>
                    {aff.status === 'pending' ? (
                      <Button variant="ghost" size="icon" onClick={() => handleStatusToggle({ ...aff, status: 'inactive' })} className="text-emerald-500" title="Approve"><Check className="w-4 h-4" /></Button>
                    ) : (
                      <Button variant="ghost" size="icon" onClick={() => handleStatusToggle(aff)} title={aff.status === 'active' ? 'Deactivate' : 'Activate'}>
                        {aff.status === 'active' ? <X className="w-4 h-4 text-red-500" /> : <Check className="w-4 h-4 text-emerald-500" />}
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(aff.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded">{aff.referral_code}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tab === 'commissions' && (
        <div className="space-y-3">
          {commissions.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No commissions recorded yet.</CardContent></Card>
          ) : commissions.map(c => (
            <Card key={c.id} data-testid={`commission-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-semibold text-sm">{c.affiliate_name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">Order: {c.order_id?.slice(0, 8)}... &middot; {new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center"><p className="font-medium">${c.order_amount?.toFixed(2)}</p><p className="text-xs text-muted-foreground">Order</p></div>
                    <div className="text-center"><p className="font-medium">{c.commission_rate}%</p><p className="text-xs text-muted-foreground">Rate</p></div>
                    <div className="text-center"><p className="font-bold text-[#1A9DA5]">${c.commission_amount?.toFixed(2)}</p><p className="text-xs text-muted-foreground">Commission</p></div>
                    <Badge className={commissionStatusColor(c.status)}>{c.status}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    {c.status === 'pending' && (
                      <Button size="sm" variant="outline" onClick={() => handleCommissionStatus(c.id, 'approved')}>Approve</Button>
                    )}
                    {(c.status === 'pending' || c.status === 'approved') && (
                      <Button size="sm" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white" onClick={() => handleCommissionStatus(c.id, 'paid')}>Mark Paid</Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tab === 'payouts' && (
        <div className="space-y-3">
          {payouts.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">
              <Banknote className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>No payouts recorded yet. Click "Payout" on an affiliate to create one.</p>
            </CardContent></Card>
          ) : payouts.map(p => (
            <Card key={p.id} data-testid={`payout-card-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-semibold text-sm">{p.affiliate_name}</p>
                    <p className="text-xs text-muted-foreground">{p.affiliate_email}</p>
                    <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center">
                      <p className="text-lg font-bold text-emerald-600">${p.amount?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">Amount</p>
                    </div>
                    <div className="text-center">
                      <Badge variant="outline" className="capitalize">{p.method || 'N/A'}</Badge>
                      <p className="text-xs text-muted-foreground mt-0.5">Method</p>
                    </div>
                    {p.payment_details?.paypal_email && (
                      <p className="text-xs text-muted-foreground">{p.payment_details.paypal_email}</p>
                    )}
                    {p.payment_details?.bank_name && (
                      <p className="text-xs text-muted-foreground">{p.payment_details.bank_name} ****{p.payment_details.bank_account_number}</p>
                    )}
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-600">{p.status}</Badge>
                </div>
                {p.notes && <p className="text-xs text-muted-foreground mt-2 italic">Note: {p.notes}</p>}
                {p.commission_ids?.length > 0 && <p className="text-xs text-muted-foreground mt-1">{p.commission_ids.length} commission(s) included</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Affiliate Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditAffiliate(null); }}>
        <DialogContent className="max-w-md" data-testid="affiliate-dialog">
          <DialogHeader>
            <DialogTitle>{editAffiliate ? 'Edit Affiliate' : 'Create Affiliate'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium">Name</label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="affiliate-name-input" /></div>
            {!editAffiliate && (
              <>
                <div><label className="text-sm font-medium">Email</label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="affiliate-email-input" /></div>
                <div><label className="text-sm font-medium">Password</label><Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} data-testid="affiliate-password-input" /></div>
              </>
            )}
            {editAffiliate && (
              <div>
                <label className="text-sm font-medium">Reset Password <span className="text-muted-foreground text-xs">(leave empty to keep current)</span></label>
                <Input type="password" value={form.new_password || ''} onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))} placeholder="Enter new password" data-testid="affiliate-reset-password" />
              </div>
            )}
            <div><label className="text-sm font-medium">Company</label><Input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} /></div>
            <div><label className="text-sm font-medium">Website</label><Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">Commission Rate (%)</label><Input type="number" min={0} max={100} value={form.commission_rate} onChange={e => setForm(f => ({ ...f, commission_rate: e.target.value }))} data-testid="affiliate-rate-input" /></div>
              <div><label className="text-sm font-medium">Status</label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="affiliate-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleSave} className="w-full bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="save-affiliate-btn">
              {editAffiliate ? 'Update Affiliate' : 'Create Affiliate'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payout Dialog */}
      <Dialog open={payoutDialogOpen} onOpenChange={setPayoutDialogOpen}>
        <DialogContent className="max-w-md" data-testid="payout-dialog">
          <DialogHeader>
            <DialogTitle>Record Payout — {payoutTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {payableData && (
              <div className="p-3 rounded-lg bg-muted text-sm">
                <p className="font-medium">Payable commissions: {payableData.commissions.length}</p>
                <p className="text-lg font-bold text-[#1A9DA5]">Total: ${payableData.total_payable.toFixed(2)}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Payout Amount ($)</label>
              <Input type="number" step="0.01" min="0" value={payoutForm.amount} onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))} data-testid="payout-amount-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Payment Method</label>
              <Select value={payoutForm.method} onValueChange={v => setPayoutForm(f => ({ ...f, method: v }))}>
                <SelectTrigger data-testid="payout-method-select"><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
              {payoutTarget && payoutForm.method === 'paypal' && payoutTarget.paypal_email && (
                <p className="text-xs text-muted-foreground mt-1">PayPal: {payoutTarget.paypal_email}</p>
              )}
              {payoutTarget && payoutForm.method === 'bank' && payoutTarget.bank_name && (
                <p className="text-xs text-muted-foreground mt-1">Bank: {payoutTarget.bank_name} — {payoutTarget.bank_account_name}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Notes <span className="text-muted-foreground">(optional)</span></label>
              <Textarea value={payoutForm.notes} onChange={e => setPayoutForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. PayPal transaction ID, reference number" rows={2} data-testid="payout-notes-input" />
            </div>
            <Button onClick={handleCreatePayout} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-payout-btn">
              <Banknote className="w-4 h-4 mr-1" />Record Payout of ${parseFloat(payoutForm.amount || 0).toFixed(2)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
